import React, { useState, useEffect, useCallback, type ComponentType } from "react";
import dayjs from "dayjs";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import packageJson from "../../package.json";

type MetaJson = { buildDate: number | string };

type PackageJsonWithBuild = typeof packageJson & { buildDate?: number | string };

const POLL_INTERVAL_MS = 15 * 60 * 1000;

const normalizeBuildDate = (value: number | string | undefined): string => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const buildDateGreaterThan = (latestDate: string, currentDate: string): boolean => {
  if (!latestDate || !currentDate) {
    return false;
  }

  const latestNum = Number(latestDate);
  const currentNum = Number(currentDate);

  // buildDate is an epoch ms timestamp written by update-build.js
  if (Number.isFinite(latestNum) && Number.isFinite(currentNum)) {
    return latestNum > currentNum;
  }

  return dayjs(latestDate).isAfter(dayjs(currentDate));
};

async function fetchMetaBuildDate(): Promise<number | string | undefined> {
  const response = await fetch(`/meta.json?t=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) {
    throw new Error("meta.json request failed");
  }
  const meta = (await response.json()) as MetaJson;
  return meta.buildDate;
}

function withClientVersionCheck<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  function ClientVersionCheckWrapper(props: P) {
    const [bootChecked, setBootChecked] = useState(() => Boolean(import.meta.env.DEV));
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const currentVersionDate = normalizeBuildDate((packageJson as PackageJsonWithBuild).buildDate);

    const checkForUpdate = useCallback(async () => {
      if (import.meta.env.DEV) {
        return;
      }
      try {
        const latestRaw = await fetchMetaBuildDate();
        const latestVersionDate = normalizeBuildDate(latestRaw);
        const stale = buildDateGreaterThan(latestVersionDate, currentVersionDate);
        setUpdateAvailable(stale);
      } catch {
        // Keep previous updateAvailable on transient network errors.
      } finally {
        setBootChecked(true);
      }
    }, [currentVersionDate]);

    useEffect(() => {
      if (import.meta.env.DEV) {
        return;
      }
      void checkForUpdate();
    }, [checkForUpdate]);

    useEffect(() => {
      if (import.meta.env.DEV) {
        return;
      }
      const onVisible = () => {
        if (document.visibilityState === "visible") {
          void checkForUpdate();
        }
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => document.removeEventListener("visibilitychange", onVisible);
    }, [checkForUpdate]);

    useEffect(() => {
      if (import.meta.env.DEV) {
        return;
      }
      const id = window.setInterval(() => void checkForUpdate(), POLL_INTERVAL_MS);
      return () => window.clearInterval(id);
    }, [checkForUpdate]);

    const refreshCacheAndReload = async () => {
      // Must finish cache/SW cleanup before reload. A fire-and-forget delete
      // races the reload and the cache-first SW keeps serving the old app
      // (toast returns until a second refresh).
      try {
        if (typeof caches !== "undefined") {
          const names = await caches.keys();
          await Promise.all(names.map((name) => caches.delete(name)));
        }
        if ("serviceWorker" in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((registration) => registration.unregister()));
        }
      } catch {
        // Still reload so the user can pick up updates even if cleanup fails.
      }
      window.location.reload();
    };

    if (!import.meta.env.DEV && !bootChecked) {
      return null;
    }

    return (
      <>
        <Component {...props} />
        <Snackbar
          open={updateAvailable}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={null}
          onClose={(_, reason) => {
            if (reason === "clickaway") {
              return;
            }
            setUpdateAvailable(false);
          }}
          sx={{ bottom: { xs: 16, sm: 24 } }}
        >
          <Alert
            variant="filled"
            onClose={() => setUpdateAvailable(false)}
            sx={(theme) => ({
              width: "100%",
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "& .MuiAlert-icon": {
                color: theme.palette.primary.contrastText,
              },
            })}
            action={
              <Button color="inherit" size="small" onClick={refreshCacheAndReload}>
                Refresh
              </Button>
            }
          >
            A new version of the app is available.
          </Alert>
        </Snackbar>
      </>
    );
  }

  return ClientVersionCheckWrapper;
}

export default withClientVersionCheck;
